import { logger as baseLogger } from "matrix-js-sdk/src/logger";
import { TABLE_ACCOUNT_NAME_STORAGE, TABLE_PICKLEKEY_NAME_STORAGE } from "../dth/constants";

const logger = baseLogger.getChild("IDB");

/**
 * Retrieves the IndexedDB factory object.
 *
 * @returns {IDBFactory | undefined} The IndexedDB factory object if available, or undefined if it is not supported.
 */
export function getIDBFactory(): IDBFactory | undefined {
    // IndexedDB loading is lazy for easier testing.

    // just *accessing* _indexedDB throws an exception in firefox with
    // indexeddb disabled.
    try {
        // `self` is preferred for service workers, which access this file's functions.
        // We check `self` first because `window` returns something which doesn't work for service workers.
        // Note: `self?.indexedDB ?? window.indexedDB` breaks in service workers for unknown reasons.
        return self?.indexedDB ? self.indexedDB : window.indexedDB;
    } catch { }
}

let idb: IDBDatabase | null = null;

/**
 * Promise condivisa per l'inizializzazione del DB.
 * Garantisce che idbInit() venga eseguita una sola volta anche in caso
 * di chiamate parallele (es. idbLoad + idbSave simultanei al primo avvio).
 * Se è null, nessuna inizializzazione è in corso.
 */
let idbInitPromise: Promise<void> | null = null;

/**
 * Forza la pulizia dello stato IndexedDB.
 * Chiude la connessione e resetta le variabili di stato.
 */
export function forceIdbCleanup(): void {
    if (idb) {
        try {
            idb.close();
        } catch (e) {
            logger.warn("Error closing DB during cleanup", e);
        }
    }
    idb = null;
    idbInitPromise = null;
}

async function idbInit(): Promise<void> {
    // Se l'inizializzazione è già in corso, aspetta quella esistente
    // invece di aprirne una seconda in parallelo
    if (idbInitPromise) {
        return idbInitPromise;
    }

    if (!getIDBFactory()) {
        throw new Error("IndexedDB not available");
    }

    idbInitPromise = new Promise<void>((resolve, reject) => {
        const request = getIDBFactory()!.open("matrix-react-sdk", 1);

        request.onerror = (): void => {
            forceIdbCleanup(); // Permetti retry in caso di errore e chiudi la connessione
            reject(request.error);
        };

        request.onsuccess = (): void => {
            idb = request.result;

            // Se la connessione viene chiusa inaspettatamente (es. db eliminato dal browser),
            // resetta lo stato in modo che la prossima operazione reinizializzi correttamente
            idb.onclose = (): void => {
                logger.warn("Database connection closed unexpectedly, will reinitialize on next access");
                forceIdbCleanup();
            };

            // Gestione errori di versione (es. altro tab ha aggiornato il DB)
            idb.onversionchange = (): void => {
                logger.warn("Database version changed, closing connection");
                forceIdbCleanup();
            };

            resolve();
        };

        request.onupgradeneeded = (): void => {
            const db = request.result;
            db.createObjectStore(TABLE_PICKLEKEY_NAME_STORAGE);
            db.createObjectStore(TABLE_ACCOUNT_NAME_STORAGE);
        };
    });

    return idbInitPromise;
}

/**
 * Assicura che il DB sia inizializzato prima di eseguire un'operazione.
 * Punto unico di accesso a idbInit(), evita duplicazioni nei metodi pubblici.
 */
async function ensureIdb(): Promise<void> {
    if (!idb) {
        await idbInit();
    }
}

async function idbTransaction(
    table: string,
    mode: IDBTransactionMode,
    fn: (objectStore: IDBObjectStore) => IDBRequest<any>,
    timeoutMs: number = 5000 // default 5s (aumentato perché ora aspettiamo oncomplete)
): Promise<any> {
    await ensureIdb();

    return new Promise((resolve, reject) => {
        if (!idb) {
            reject(new Error("IndexedDB not initialized"));
            return;
        }
        const txn = idb.transaction([table], mode);

        // Flag unico condiviso tra tutti gli handler per garantire
        // che la Promise venga settled una sola volta.
        let settled = false;
        // Il risultato della request viene catturato in onsuccess,
        // ma la Promise viene risolta SOLO in oncomplete (vedi sotto).
        let requestResult: any;
        const start = Date.now();

        const settle = (fn: () => void): void => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            fn();
        };

        // Il timer misura il tempo totale incluso il commit della transazione.
        // Usare oncomplete come punto di risoluzione è fondamentale: senza questo,
        // la Promise risolverebbe su request.onsuccess mentre la transazione è ancora
        // in auto-commit. La transazione successiva (anche in readwrite sulla stessa
        // tabella) verrebbe quindi creata e messa in coda dall'IDB engine prima che
        // la precedente abbia rilasciato i lock — causando timeout a cascata.
        const timer = setTimeout(() => {
            settle(() => {
                try { txn.abort(); } catch { }
                reject(new Error(`IndexedDB transaction timeout after ${timeoutMs}ms on table "${table}"`));
            });
        }, timeoutMs);

        txn.onerror = (event): void => {
            settle(() => {
                console.error(`[IDB] Transaction error on table "${table}":`, event);
                reject(txn.error ?? event);
            });
        };

        // onabort viene chiamato quando il timeout ha già abortito la transazione,
        // oppure quando l'IDB engine l'ha abortita per errori interni.
        // Se è già settled (timeout), questo è un no-op.
        txn.onabort = (): void => {
            settle(() => {
                reject(txn.error ?? new Error(`IndexedDB transaction aborted on table "${table}"`));
            });
        };

        // Punto di risoluzione corretto: la transazione è committata e i lock sono
        // rilasciati. Solo a questo punto la Promise risolve, garantendo che la
        // prossima transazione readwrite sulla stessa tabella possa partire subito.
        txn.oncomplete = (): void => {
            settle(() => {
                const elapsed = Date.now() - start;
                if (elapsed > 1000) {
                    console.warn(`[IDB] Slow transaction on table "${table}": ${elapsed}ms`);
                }
                resolve(requestResult);
            });
        };

        const objectStore = txn.objectStore(table);
        const request = fn(objectStore);

        request.onerror = (event): void => {
            settle(() => {
                console.error(`[IDB] Request error on table "${table}":`, event);
                reject(request.error ?? event);
            });
        };

        // Cattura il risultato della request (es. il valore letto in idbLoad),
        // ma NON risolve ancora la Promise. Si aspetta oncomplete.
        request.onsuccess = (): void => {
            requestResult = request.result;
        };
    });
}

/**
 * Loads an item from an IndexedDB table within the underlying `matrix-react-sdk` database.
 *
 * If IndexedDB access is not supported in the environment, an error is thrown.
 *
 * @param {string} table The name of the object store in IndexedDB.
 * @param {string | string[]} key The key where the data is stored.
 * @returns {Promise<any>} A promise that resolves with the retrieved item from the table.
 */
export async function idbLoad(table: string, key: string | string[]): Promise<any> {
    await ensureIdb();
    return idbTransaction(table, "readonly", (objectStore) => objectStore.get(key));
}

/**
 * Saves data to an IndexedDB table within the underlying `matrix-react-sdk` database.
 *
 * If IndexedDB access is not supported in the environment, an error is thrown.
 *
 * @param {string} table The name of the object store in the IndexedDB.
 * @param {string|string[]} key The key to use for storing the data.
 * @param {*} data The data to be saved.
 * @returns {Promise<void>} A promise that resolves when the data is saved successfully.
 */
export async function idbSave(table: string, key: string | string[], data: any): Promise<void> {
    await ensureIdb();
    return idbTransaction(table, "readwrite", (objectStore) => objectStore.put(data, key));
}

/**
 * Deletes a record from an IndexedDB table within the underlying `matrix-react-sdk` database.
 *
 * If IndexedDB access is not supported in the environment, an error is thrown.
 *
 * @param {string} table The name of the object store where the record is stored.
 * @param {string|string[]} key The key of the record to be deleted.
 * @returns {Promise<void>} A Promise that resolves when the record(s) have been successfully deleted.
 */
export async function idbDelete(table: string, key: string | string[]): Promise<void> {
    await ensureIdb();
    return idbTransaction(table, "readwrite", (objectStore) => objectStore.delete(key));
}

/**
 * Clears all records from an IndexedDB table within the underlying `matrix-react-sdk` database.
 *
 * If IndexedDB access is not supported in the environment, an error is thrown.
 *
 * @param {string} table The name of the object store where the records are stored.
 * @returns {Promise<void>} A Promise that resolves when the record(s) have been successfully deleted.
 */
export async function idbClear(table: string): Promise<void> {
    await ensureIdb();
    return idbTransaction(table, "readwrite", (objectStore) => objectStore.clear());
}